import { BottomSheet, List, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { useGetWorkerShifts } from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShifts";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { parseISO } from "date-fns";
import { useHistory } from "react-router-dom";

import { useShiftModalsDataContext } from "../../../ShiftDiscovery/useShiftModalsDataContext";
import { useBundleOffers } from "../hooks/useBundleOffers";
import { useShiftBundleSelection } from "../hooks/useShiftBundleSelection";
import { BundleFailedLoadingSheet } from "./BundleFailedLoadingSheet";
import { SelectableShiftInfoListItem } from "./SelectableShiftInfoListItem";
import { SelectionFooter } from "./SelectionFooter";
import { SelectionHeader } from "./SelectionHeader";
import { SelectionSheetSkeleton } from "./SelectionSheetSkeleton";

export interface SelectionSheetProps {
  modalState: UseModalState;
}

export function SelectionSheet(props: SelectionSheetProps) {
  const { modalState } = props;

  const history = useHistory();
  const worker = useDefinedWorker();

  const { shift, bundle, isLoadingShiftModalsData, isBundleLoading } = useShiftModalsDataContext();

  const { shifts: workerShifts } = useGetWorkerShifts({ worker });
  const bookedShifts = workerShifts.map((workerShift) => ({
    start: workerShift.start,
    end: workerShift.end,
  }));

  const bundleShifts = bundle?.shifts ?? [];
  const sortedBundleShifts = [...bundleShifts].sort(
    (a, b) => parseISO(a.attributes.start).getTime() - parseISO(b.attributes.start).getTime()
  );

  const {
    bundleShiftPays,
    offersIdMap,
    isLoading: isBundleOfferLoading,
  } = useBundleOffers({
    bundle,
    requiresLunchBreak: shift?.workplace?.attributes.requiresLunchBreak ?? false,
  });

  const {
    selectedShiftIds,
    selectedCount,
    disabledShiftMap,
    toggleShift,
    totalSelectedPayInMinorUnits,
    isBookingAllowed,
    isConfirmationMode,
  } = useShiftBundleSelection({
    bundleShifts,
    bookedShifts,
    requiredShiftCount: bundle?.requiredShiftCount ?? 0,
    totalShiftCount: bundle?.totalShiftCount ?? 1,
    bundleShiftPays,
  });

  const hasRequiredData =
    isDefined(shift) && isDefined(bundle) && bundleShiftPays.size === bundle.totalShiftCount;
  const workplaceTimezone = shift?.workplace?.attributes?.location?.timezone;

  const isLoading = isLoadingShiftModalsData || isBundleOfferLoading || isBundleLoading;

  if (isLoading) {
    return (
      <BottomSheet modalState={modalState} paperSx={{ height: "100%" }}>
        <SelectionSheetSkeleton
          onBack={() => {
            history.goBack();
          }}
        />
      </BottomSheet>
    );
  }

  if (!hasRequiredData) {
    return <BundleFailedLoadingSheet modalState={modalState} bundleShiftPays={bundleShiftPays} />;
  }

  return (
    <BottomSheet
      modalState={modalState}
      paperSx={{ height: "100%" }}
      header={
        <SelectionHeader
          requiredShiftCount={bundle.requiredShiftCount}
          totalShiftCount={bundle.totalShiftCount}
          onBack={() => {
            history.goBack();
          }}
        />
      }
      footer={
        <SelectionFooter
          shift={shift}
          bundle={bundle}
          selectedShiftIds={selectedShiftIds}
          selectedCount={selectedCount}
          totalSelectedPayInMinorUnits={totalSelectedPayInMinorUnits}
          isBookingAllowed={isBookingAllowed}
          offersIdMap={offersIdMap}
          bundleShiftPays={bundleShiftPays}
        />
      }
    >
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <List
          sx={{
            paddingX: 6,
            paddingTop: 1,
            paddingBottom: 5,
            gap: 4,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {sortedBundleShifts.map((bundleShift) => {
            const disabledInfo = disabledShiftMap.get(bundleShift.id);
            const totalPayInMinorUnits = bundleShiftPays.get(bundleShift.id)?.totalPayInMinorUnits;
            return (
              <SelectableShiftInfoListItem
                key={bundleShift.id}
                shiftId={bundleShift.id}
                timeSlot={bundleShift.attributes.timeSlot}
                startIso={bundleShift.attributes.start}
                endIso={bundleShift.attributes.end}
                payInMinorUnits={totalPayInMinorUnits}
                workplaceTimezone={workplaceTimezone}
                isSelected={selectedShiftIds.has(bundleShift.id)}
                isDisabled={isDefined(disabledInfo)}
                isReadOnly={isConfirmationMode}
                disabledReason={disabledInfo?.reason}
                onToggle={toggleShift}
              />
            );
          })}
        </List>
      </Box>
    </BottomSheet>
  );
}
